import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { EmergencySituationType } from "../../../../types/emergency";

import {
  getSituation,
  getUncertSituation,
} from "../../../../services/emergencyServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";
import useEvacuationManger from "../../../../hooks/useEvacuationManager";

import PrimaryButton from "../../../../components/Button/Primary";
import NaverMapWithSkeleton from "../../NaverMapWithSkeleton";
import useGPSInstant from "../../../../hooks/useGPSInstant";
import PinchZoomImage from "../../../../components/PinchZoomImage";
import { findOptimizedImgUrl } from "../../../../libs/image";

type Props = {};

export default function DoneResult(props: Props) {
  const params = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [simplePopupComponent, { openError }] = useSimplePopup({});
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [isManager, isUser, { user }] = useEvacuationManger();
  const [gpsResult, callGps] = useGPSInstant({});
  const [isShelterImgOpen, setIsShelterImgOpen] = useState<boolean>(false);

  const [situation, setSituation] = useState<
    EmergencySituationType | undefined
  >(state?.situation ?? {});

  const loadUserSituation = async () => {
    setIsLoading(true);
    try {
      const response = await getSituation();
      if (response.status === HttpStatusCode.Ok) {
        if (response.data.status === "IN_PROGRESS") {
          navigate("/evacuation/situation");
          return;
        } else {
          setSituation(response.data);
        }
      } else {
        throw new Error("상황 정보를 불러오지 못했습니다.");
      }
    } catch (e: any) {
      openError(e, "상황 정보를 불러오지 못했습니다.", {
        onClick: loadUserSituation,
        buttonText: "재시도",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadUncertSituation = async () => {
    setIsLoading(true);

    try {
      const response = await getUncertSituation(params.id as string);
      if (response.status === HttpStatusCode.Ok) {
        if (response.data.status === "IN_PROGRESS") {
          navigate(`/uncert-evacuation/${params.id}`);
          return;
        } else {
          setSituation(response.data);
        }
      } else {
        throw new Error("상황 정보를 불러오지 못했습니다.");
      }
    } catch (e: any) {
      openError(e, "상황 정보를 불러오지 못했습니다.", {
        onClick: loadUncertSituation,
        buttonText: "재시도",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (state?.situation) return;

    if (params.id) {
      loadUncertSituation();
    } else {
      loadUserSituation();
    }
  }, [isUser, user.isLoading, params.id]);

  const handleClickShelterImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsShelterImgOpen(true);
  };
  const handleCloseShelterImg = (e: React.MouseEvent) => {
    setIsShelterImgOpen(false);
  };

  return (
    <>
      {loadingComponent}
      <div className="flex flex-col justify-center h-screen gap-5">
        <NaverMapWithSkeleton
          situation={situation}
          unUseBottomText
          cert={params.id ? false : true}
          gpsResult={gpsResult}
          callGps={callGps}
          handleClickShelterImg={handleClickShelterImg}
        />
        <p className="w-full text-center font-black text-2xl">상황 종료</p>
        <p className="w-full text-center text-[16px] mb-[15px] font-semibold whitespace-pre-line leading-6">
          {
            "여러분의 도움으로\n 안전하게 상황을 마무리할 수 있었습니다.\n적극적인 참여와 통제에 따라주셔서\n 대단히 감사합니다.\n"
          }
        </p>
        {isUser && (
          <div className="fixed bottom-0 bg-white w-full h-[50px]">
            <PrimaryButton
              className="text-white h-[50px] w-full flex justify-center items-center text-base font-medium leading-[19px]"
              onClick={() => navigate("/view")}
            >
              닫기
            </PrimaryButton>
          </div>
        )}
      </div>
      {simplePopupComponent}
      {situation &&
        situation.shelterImg &&
        situation.shelterImg.items.length > 0 && (
          <PinchZoomImage
            imageList={[
              findOptimizedImgUrl(situation?.shelterImg?.items) as string,
            ]}
            isOpen={isShelterImgOpen}
            onClose={handleCloseShelterImg}
          />
        )}
    </>
  );
}
